import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useCurrentOrganization } from '@/contexts/OrganizationContext';
import { toast } from 'sonner';
import { geocodeAddress } from '@/utils/geocoding/geocodeAddress';
import { usePlanUsage } from './usePlanUsage';

const ADDRESS_FIELDS = ['address_line', 'city', 'zip_code', 'province', 'country_code'] as const;

/**
 * Geocoding fire-and-forget dopo create/update di un cliente.
 * Non blocca l'UX: se Nominatim fallisce, il cliente resta senza coords
 * e il bottone "Geocodifica" sulla mappa lo recupera in batch.
 */
async function geocodeCustomerInBackground(
  customerId: string,
  addr: {
    address_line?: string | null;
    city?: string | null;
    zip_code?: string | null;
    province?: string | null;
    country_code?: string | null;
  },
): Promise<void> {
  try {
    const r = await geocodeAddress({
      addressLine: addr.address_line,
      city: addr.city,
      zipCode: addr.zip_code,
      province: addr.province,
      countryCode: addr.country_code ?? 'IT',
    });
    if (!r) return;
    await supabase
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      .from('customers' as any)
      .update({
        latitude: r.latitude,
        longitude: r.longitude,
        geocoded_at: new Date().toISOString(),
      })
      .eq('id', customerId);
  } catch {
    // silenzioso: non vogliamo errori sulla creazione cliente
  }
}

export type CustomerType = 'individual' | 'company';

export interface Customer {
  id: string;
  organization_id: string;
  name: string;
  type: CustomerType;
  vat_number: string | null;
  fiscal_code: string | null;
  address_line: string | null;
  city: string | null;
  zip_code: string | null;
  province: string | null;
  country_code: string | null;
  phone: string | null;
  email: string | null;
  pec: string | null;
  sdi_code: string | null;
  notes: string | null;
  latitude: number | null;
  longitude: number | null;
  geocoded_at: string | null;
  created_at: string;
  updated_at: string;
  /** UUID utente che ha creato il record. NULL su record legacy. Usato per RLS scope TEC. */
  created_by: string | null;
}

export type CustomerInput = Omit<Customer,
  'id' | 'organization_id' | 'created_at' | 'updated_at'
>;

export function useCustomers() {
  const { currentOrganizationId } = useCurrentOrganization();
  return useQuery<Customer[]>({
    queryKey: ['customers', currentOrganizationId],
    enabled: !!currentOrganizationId,
    staleTime: 60 * 1000,
    queryFn: async () => {
      if (!currentOrganizationId) return [];
      const { data, error } = await supabase
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        .from('customers' as any)
        .select('*')
        .eq('organization_id', currentOrganizationId)
        .order('name');
      if (error) {
        console.error('[useCustomers] error:', error);
        return [];
      }
      return (data as unknown as Customer[]) ?? [];
    },
  });
}

export function useCustomer(customerId: string | null | undefined) {
  return useQuery<Customer | null>({
    queryKey: ['customer', customerId],
    enabled: !!customerId,
    staleTime: 60 * 1000,
    queryFn: async () => {
      if (!customerId) return null;
      const { data, error } = await supabase
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        .from('customers' as any)
        .select('*')
        .eq('id', customerId)
        .maybeSingle();
      if (error) return null;
      return (data as unknown as Customer) ?? null;
    },
  });
}

export function useCreateCustomer() {
  const queryClient = useQueryClient();
  const { currentOrganizationId } = useCurrentOrganization();
  // F33B-3 — gate piano: blocca su Solo se al tetto (15 clienti).
  const { customers: customersUsage } = usePlanUsage();
  return useMutation({
    mutationFn: async (input: Partial<CustomerInput> & { name: string }) => {
      if (!currentOrganizationId) throw new Error('Nessuna organizzazione attiva');
      if (customersUsage.atLimit) {
        const err = new Error(
          `Hai raggiunto il limite di ${customersUsage.max} clienti del piano Solo. Passa a Studio per illimitati.`,
        );
        (err as Error & { code?: string }).code = 'PLAN_LIMIT_REACHED';
        throw err;
      }
      const { data, error } = await supabase
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        .from('customers' as any)
        .insert({
          organization_id: currentOrganizationId,
          name: input.name,
          type: input.type ?? 'individual',
          vat_number: input.vat_number || null,
          fiscal_code: input.fiscal_code || null,
          address_line: input.address_line || null,
          city: input.city || null,
          zip_code: input.zip_code || null,
          province: input.province || null,
          country_code: input.country_code || 'IT',
          phone: input.phone || null,
          email: input.email || null,
          pec: input.pec || null,
          sdi_code: input.sdi_code || null,
          notes: input.notes || null,
        })
        .select()
        .single();
      if (error) throw error;
      return data as unknown as Customer;
    },
    onSuccess: (created) => {
      queryClient.invalidateQueries({ queryKey: ['customers', currentOrganizationId] });
      // F33B-3: invalidate plan-usage per aggiornare il counter "X/15" sul bottone
      // immediatamente dopo create — altrimenti l'utente può creare oltre il limite
      // finché la cache (staleTime 30s) non scade.
      queryClient.invalidateQueries({ queryKey: ['plan-usage', currentOrganizationId] });
      toast.success('Cliente creato');
      // Geocoding in background: l'utente vede subito il toast, le coords
      // arrivano dopo 0.5-2s e la prossima apertura mappa le mostrera'.
      void geocodeCustomerInBackground(created.id, created).then(() => {
        queryClient.invalidateQueries({ queryKey: ['customers-map'] });
      });
    },
    onError: (err: Error & { code?: string }) => {
      if (err.code === 'PLAN_LIMIT_REACHED') {
        toast.error('Limite clienti raggiunto', {
          description: err.message,
          action: {
            label: 'Passa a Studio',
            onClick: () => { window.location.href = '/settings/billing'; },
          },
        });
      } else {
        toast.error(`Errore: ${err.message}`);
      }
    },
  });
}

export function useUpdateCustomer() {
  const queryClient = useQueryClient();
  const { currentOrganizationId } = useCurrentOrganization();
  return useMutation({
    mutationFn: async ({ id, ...patch }: Partial<CustomerInput> & { id: string }) => {
      const cleaned: Record<string, unknown> = {};
      const addressChanged = ADDRESS_FIELDS.some((f) => f in patch);
      for (const [k, v] of Object.entries(patch)) {
        cleaned[k] = v === '' ? null : v;
      }
      // Se l'utente modifica un campo address, invalido le coords cosi'
      // il successivo geocoding background le ricalcola da zero.
      if (addressChanged) {
        cleaned.latitude = null;
        cleaned.longitude = null;
        cleaned.geocoded_at = null;
      }
      const { error } = await supabase
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        .from('customers' as any)
        .update(cleaned)
        .eq('id', id);
      if (error) throw error;
      return { id, addressChanged, patch: cleaned };
    },
    onSuccess: (result, vars) => {
      queryClient.invalidateQueries({ queryKey: ['customers', currentOrganizationId] });
      queryClient.invalidateQueries({ queryKey: ['customer', vars.id] });
      toast.success('Cliente aggiornato');
      // Re-geocoda in background solo se l'address e' cambiato.
      if (result?.addressChanged) {
        void geocodeCustomerInBackground(vars.id, {
          address_line: vars.address_line,
          city: vars.city,
          zip_code: vars.zip_code,
          province: vars.province,
          country_code: vars.country_code,
        }).then(() => {
          queryClient.invalidateQueries({ queryKey: ['customers-map'] });
        });
      }
    },
    onError: (err: Error) => toast.error(`Errore: ${err.message}`),
  });
}

export function useDeleteCustomer() {
  const queryClient = useQueryClient();
  const { currentOrganizationId } = useCurrentOrganization();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        .from('customers' as any)
        .delete()
        .eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['customers', currentOrganizationId] });
      toast.success('Cliente eliminato');
    },
    onError: (err: Error) => toast.error(`Errore: ${err.message}`),
  });
}
